/* eslint-disable react/prop-types */
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Typography,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import Table from 'components/Table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMutable } from 'utils/estadosGlobales';
import { onError } from 'utils/handlers';

import {
  deleteZonaDeDiligencia,
  getZonasDeDiligencia,
  patchZonaDeDiligencia,
  postZonaDeDiligencia,
} from 'api/pae/zonasDeDiligencia';
import keyGenerator from 'utils/keyGenerator';
import { capitalize } from 'utils/normalizers';

export const permissionConfig = {
  permissionModel: 'zonadediligenciapae',
};

const ZonasDeDiligencia = ({ permission }) => {
  const { estadosGlobales } = useSelector((state) => state.catalogs);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    const res = await getZonasDeDiligencia(null, false);
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      const _data = await getZonasDeDiligencia();
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchZonaDeDiligencia(key, values)
        : postZonaDeDiligencia(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteZonaDeDiligencia(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    keyGenerator(data, form);
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 500,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Visible',
      dataIndex: 'visible_en_presentacion',
      key: 'visible_en_presentacion',
      width: 100,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Seleccionable',
      dataIndex: 'seleccionable_en_presentacion',
      key: 'seleccionable_en_presentacion',
      width: 150,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Orden',
      dataIndex: 'orden_de_presentacion',
      key: 'orden_de_presentacion',
      width: 100,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 300,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (value) => {
        const estado = estadosGlobales.find((eg) => eg.id === value);
        return (
          <Typography.Text type={estado?.descripcion?.toLowerCase().includes('activo') ? 'success' : 'default'}>
            {estado?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
  ];

  const rules = {
    required: [{ required: true }],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        {!visible && (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            permission={permission}
            hasAllViewPermission
            allowColumnSelector={false}
            deleteText="Cancelar"
          />
        )}
        {visible && (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Zona de Diligencia`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
                selectedRowKeys={selectedRowKeys}
                callback={(id) => {
                  const [key] = selectedRowKeys;
                  const normalized = data.map((e) => {
                    if (e.id === key) {
                      const match = estadosGlobales.find((eg) => eg.id === id);
                      form.setFieldsValue({ estados_globales: match.id });
                      setMutable(true);
                      return { ...e, estados_globales: id };
                    }
                    return e;
                  });
                  setData(normalized);
                }}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              initialValues={{
                estados_globales: 1,
                visible_en_presentacion: true,
                seleccionable_en_presentacion: true,
              }}
            >
              {/* Sección de Información Básica */}
              <Row gutter={[10, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="clave"
                    label="Clave"
                  >
                    <Input disabled={selectedRowKeys.length} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={16}>
                  <Form.Item
                    name="descripcion"
                    rules={rules.required}
                    label="Descripción"
                    normalize={capitalize}
                  >
                    <Input.TextArea
                      disabled={!mutable}
                      allowClear
                      maxLength={300}
                      autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Sección de Jerarquía y Dependencias */}
              <Row gutter={[10, 10]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="zona_superior_dependiente"
                    label="Zona Superior Dependiente"
                    hasFeedback
                  >
                    <Select
                      dataSource={data.filter((e) => e.id !== selectedRowKeys[0])}
                      allowClear
                      disabled={!mutable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="orden_de_presentacion"
                    label="Orden de Presentación"
                  >
                    <Input
                      disabled={!mutable}
                      allowClear
                      type="number"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Sección de Configuración de Presentación */}
              <Row gutter={[10, 10]}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="visible_en_presentacion"
                    label="Visible en Presentación"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      trueFalse
                      allowClear
                      disabled={!mutable}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="seleccionable_en_presentacion"
                    label="Seleccionable en Presentación"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      trueFalse
                      allowClear
                      disabled={!mutable}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Sección de Observaciones y Estado */}
              <Row gutter={[10, 10]}>
                <Col xs={24} md={16}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                    normalize={capitalize}
                    hasFeedback
                  >
                    <Input.TextArea
                      allowClear
                      disabled={!mutable}
                      autoSize={{ minRows: 3, maxRows: 6 }}
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="estados_globales"
                    label="Estado"
                    hasFeedback
                  >
                    <Select
                      dataSource={estadosGlobales}
                      allowClear
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={` la zona de diligencia ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ZonasDeDiligencia;
